import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Link2, Check } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect HTML body (TipTap output) vs legacy Markdown
function ArticleBody({ body }) {
  const content = Array.isArray(body) ? body.join("\n\n") : (body || "");
  const isHTML = content.trim().startsWith("<");
  if (isHTML) {
    return (
      <div
        className="dn-article-body"
        data-testid="article-body"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }
  return (
    <div className="dn-article-body" data-testid="article-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

// ─── Inline SVGs for brand icons (lucide doesn't carry LinkedIn/X logos) ─────
function LinkedInIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
      <rect x="2" y="9" width="4" height="12"/>
      <circle cx="4" cy="4" r="2"/>
    </svg>
  );
}

function XIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
    </svg>
  );
}

// ─── Share bar ────────────────────────────────────────────────────────────────
function ShareBar({ title, size = "sm" }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || "");

  function copyLink() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const isLarge = size === "lg";
  const base = `flex items-center gap-1.5 font-lora tracking-widest uppercase transition-all border`;
  const sm  = `${base} text-[9px] px-3 py-1.5`;
  const lg  = `${base} text-[10px] px-4 py-2`;
  const cls = isLarge ? lg : sm;

  return (
    <div className={`flex items-center gap-${isLarge ? "3" : "2"} flex-wrap`} data-testid="share-bar">
      {isLarge && (
        <span className="font-lora text-[9px] tracking-[0.28em] uppercase text-navy/30 mr-1">Share</span>
      )}
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`${cls} border-[#0A66C2]/30 text-[#0A66C2]/70 hover:bg-[#0A66C2]/5 hover:border-[#0A66C2]/60`}
        data-testid="share-linkedin"
        aria-label="Share on LinkedIn"
      >
        <LinkedInIcon size={isLarge ? 13 : 11} /> LinkedIn
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`${cls} border-navy/20 text-navy/50 hover:bg-navy/5 hover:border-navy/40`}
        data-testid="share-twitter"
        aria-label="Share on X / Twitter"
      >
        <XIcon size={isLarge ? 13 : 11} /> X
      </a>
      <button
        onClick={copyLink}
        className={`${cls} ${copied ? "border-gold/60 text-gold bg-gold/5" : "border-navy/20 text-navy/50 hover:bg-navy/5 hover:border-navy/40"}`}
        data-testid="share-copy-link"
        aria-label="Copy article link"
      >
        {copied ? <Check size={isLarge ? 13 : 11} /> : <Link2 size={isLarge ? 13 : 11} />}
        {copied ? "Copied" : "Copy Link"}
      </button>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/articles/${slug}`)
      .then(res => { setArticle(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-navy/20 border-t-navy rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-lora text-2xl text-navy mb-4">Article Not Found</h1>
        <Link to="/insights" className="text-navy/50 hover:text-navy text-sm tracking-widest uppercase">
          &larr; Back to Insights
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-ivory min-h-screen" data-testid="article-page">
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/50"></div>
        <div className="absolute inset-0 flex items-end px-6 lg:px-12 pb-8 max-w-4xl mx-auto">
          <div>
            <p className="text-gold/90 text-xs tracking-[0.3em] uppercase mb-2">{article.date} · {article.readTime}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-2xl mx-auto px-6 py-16">
        <Link to="/insights" className="inline-flex items-center gap-2 text-navy/40 hover:text-navy text-xs tracking-widest uppercase mb-8 transition-colors">
          <ArrowLeft size={12} /> Back to Insights
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
          <h1 className="font-lora text-3xl sm:text-4xl text-navy font-normal leading-tight mb-4" data-testid="article-title">
            {article.title}
          </h1>
          {article.subtitle && (
            <p className="font-lora text-xl text-navy/60 italic font-normal mb-6">{article.subtitle}</p>
          )}
          <div className="w-10 h-px bg-gold mb-8"></div>
          <ShareBar title={article.title} size="sm" />
          <div className="mt-6"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.3 }}
        >
          <ArticleBody body={article.body} />
        </motion.div>

        {/* CTA */}
        <div className="mt-16 pt-10 border-t border-soft-gray">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
            <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold">Next Step</p>
            <ShareBar title={article.title} size="lg" />
          </div>
          <h3 className="font-lora text-2xl text-navy font-normal mb-3">Ready to apply these insights?</h3>
          <p className="text-navy/60 font-serif text-sm mb-6 leading-relaxed">
            Reading is the beginning. A focused advisory session is where understanding becomes action.
          </p>
          <Link to="/booking" className="inline-flex items-center gap-2 bg-navy text-ivory px-8 py-3 text-sm tracking-widest uppercase hover:bg-navy/90 transition-all">
            Book a Session &rarr;
          </Link>
        </div>
      </article>
    </div>
  );
}
